import { TextField, TextFieldProps } from '@mui/material';
import React, {
  ChangeEventHandler,
  ClipboardEventHandler,
  FocusEventHandler,
  forwardRef,
  ForwardRefRenderFunction,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useInView } from 'react-intersection-observer';

import { getValidityProps } from './helpers';

export type Props = Omit<TextFieldProps, 'onChange' | 'onPaste'> & {
  enterKeyHint?: 'enter' | 'done' | 'go' | 'previous' | 'search' | 'send';
  externalError?: boolean;
  externalHelperText?: NonNullable<ReactNode>;
  formId?: string;
  maxLength?: HTMLInputElement['maxLength'];
  minLength?: HTMLInputElement['minLength'];
  onChange: (value: HTMLInputElement['value']) => void;
  onPaste?: (value: string) => void;
  pattern?: string;
  shouldFocus?: HTMLInputElement['autofocus'];
  value: HTMLInputElement['value'];
};

/**
 * Just a wrapper around the native input component,
 * styled according to the design
 *
 * @param props                    Props passed to the component
 * @param props.enterKeyHint       Action shown on virtual keyboard
 * @param props.externalError      External error
 * @param props.externalHelperText External helper text for error
 * @param props.formId             The formId, optional argument
 * @param props.maxLength          Maximum text length
 * @param props.minLength          Minimum text length
 * @param props.onChange           Callback to be invoked every time input changes
 * @param props.onPaste            Callback to be invoked whenever the text is pasted to input
 * @param props.pattern            Pattern for the input
 * @param props.required           Whether the input is required
 * @param props.shouldFocus        Should the input be auto focused
 * @param props.value              The value of the input
 * @param ref                      Forward ref for the input
 * @returns                        The component itself
 */
const Input: ForwardRefRenderFunction<HTMLInputElement, Props> = (
  {
    shouldFocus = false,
    enterKeyHint,
    externalError,
    externalHelperText,
    formId,
    onChange,
    onPaste,
    maxLength,
    minLength,
    pattern,
    required,
    value,
    ...restProps
  },
  ref,
) => {
  const [cursorPos, setCursorPos] = useState<number | null>(null);
  const [isTouched, setIsTouched] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [inViewRef, isInView] = useInView({
    // Element will be in view if 100% of element is in view
    threshold: 1,
  });

  useEffect(() => {
    // Focus the input if it should be focused and is in view
    if (shouldFocus && isInView) {
      inputRef?.current?.focus();
    }
  }, [shouldFocus, inputRef, isInView]);

  useEffect(() => {
    if (cursorPos !== null && inputRef.current !== null) {
      // Update cursor position if it's changed
      inputRef.current.setSelectionRange(cursorPos, cursorPos);
      setCursorPos(null);
    }
  }, [cursorPos, value]);

  /**
   * Set refs callback that allows us to use Intersection Observer along with
   * normal ref functionality like accessing the `current` property
   *
   * @param node DOM node from ref callback
   */
  const setRefs = useCallback(
    (node: HTMLInputElement) => {
      inputRef.current = node;

      if (ref) {
        if (typeof ref === 'function') {
          ref(node);
        } else {
          ref.current = node;
        }
      }

      inViewRef(node);
    },
    [inViewRef, ref],
  );

  /**
   * The user has changed the input value, so we inform the parent about it
   *
   * @param event The event that took place
   */
  const handleChange: ChangeEventHandler<HTMLInputElement> = event => {
    onChange(event.currentTarget.value);
  };

  /**
   * The user has pasted the input value, so we inform the parent about it
   *
   * @param event The event that took place
   */
  const handlePaste: ClipboardEventHandler<HTMLInputElement> = event => {
    if (onPaste !== undefined) {
      event.preventDefault();

      const pasteText = event.clipboardData.getData('text');

      const target = event.target as HTMLInputElement;
      const { selectionStart, selectionEnd, value: inputValue } = target;

      const endInputCursor = pasteText.length;
      const startCursor = selectionStart ?? endInputCursor;
      const endCursor = selectionEnd ?? endInputCursor;

      const newValue =
        inputValue.substring(0, startCursor) +
        pasteText +
        inputValue.substring(endCursor);

      onPaste(newValue);
      setCursorPos(startCursor + pasteText.length);
    }
  };

  /**
   * The user has left the field, so we set error if it's required and empty
   *
   * @param event The event that took place
   */
  const handleBlur: FocusEventHandler<HTMLInputElement> = event => {
    event.preventDefault();

    if (required !== true || isTouched) {
      return;
    }

    setIsTouched(true);
  };

  const { error, helperText } = getValidityProps(
    isTouched && required === true,
    value,
    maxLength,
  );

  const shouldShowError = externalError !== undefined ? externalError : error;
  const errorText =
    externalHelperText !== undefined ? externalHelperText : helperText;

  return (
    <TextField
      aria-invalid={shouldShowError}
      error={shouldShowError}
      // This will be default, it could be overridden
      fullWidth={true}
      helperText={errorText}
      inputRef={setRefs}
      onBlur={handleBlur}
      onChange={handleChange}
      onPaste={handlePaste}
      required={required}
      slotProps={{
        htmlInput: {
          enterKeyHint,
          form: formId,
          minLength,
          pattern,
        },
        inputLabel: {
          // This places input label in input border
          shrink: true,
        },
      }}
      value={value}
      {...restProps}
    />
  );
};

export default forwardRef(Input);
